import Sensor from "./Sensors.js";

/**
 * Pedometer: step detection from accelerometer data using band-pass filtering,
 * valley-peak-valley detection, and walking state gate.
 *
 * Key features:
 * - Band-pass filter (fast EMA - slow EMA) for rotation rejection
 * - Valley-peak-valley step model (MDPI paper approach)
 * - Walking state gate (IDLE → WALKING) to reject isolated bumps
 * - Weinberg step length estimation with cadence correction
 * - Stationary detection (dual-condition)
 * - Auto-sensitivity tuning (only in confirmed walking)
 * - GPS calibration support
 *
 * References:
 * - MDPI Sensors: "Step-Detection and Adaptive Step-Length Estimation for PDR..." https://www.mdpi.com/1424-8220/16/9/1423
 * - Weinberg (2002): step_length = K × (a_max - a_min)^0.25
 */
export class Pedometer extends Sensor {
    constructor(options = {}) {
        super();
        const C = Pedometer.CONFIG;

        // User configuration
        this._sensitivity = options.sensitivity ?? `auto`;
        this._stepLength = options.stepLength ?? 0.55; // Neutral default (adapts via amplitude scaling)
        this._weinbergK = options.weinbergK ?? 0.42;   // Base K (amplitude scaling handles small steps)

        // Detection state
        this._magnitudeRingBuffer = [];
        this._magnitudeRingHead = 0;
        this._rollingStats = { mean: 0 };
        this._baselineMagnitude = 0;
        this._magnitudeVariance = 0;
        this._lastStepTime = 0;
        this._lastSampleTime = 0;
        this._baselineInitStartTime = null;

        // Detection thresholds (set by sensitivity preset)
        this._peakThreshold = 2.0;
        this._minStepIntervalMs = 350;
        this._maxStepLength = 0.85;

        // Band-pass filter state (fast EMA - slow EMA)
        this._walkSignalFast = 0;
        this._walkSignalSlow = 0;
        this._walkSignal = 0;
        this._prevWalkSignal = 0;

        // Valley-peak-valley detection state
        this._extremaState = `seeking_valley`; // seeking_valley, seeking_peak, confirming_step
        this._lastValley = { value: 0, time: 0 };
        this._lastPeak = { value: 0, time: 0 };
        this._currentExtrema = { value: 0, time: 0 };
        this._extremaHoldCount = 0;

        // Walking state gate (IDLE → WALKING → COOLDOWN)
        this._walkingState = `idle`; // idle, walking, cooldown
        // Step candidates now store amplitude data for proper replay:
        // [{t, pvAmp, weinbergAmp, samples: [{value, time}]}]
        this._stepCandidates = [];
        this._cooldownEndTime = 0;
        this._lastWalkingStepTime = 0; // For walking timeout

        // Rotation gate state (gravity-vector angular speed)
        this._gPrev = { x: 0, y: 0, z: 0 };
        this._omega = 0; // Rotation rate (rad/s)

        // Legacy hysteresis (kept for compatibility, but valley-peak-valley is primary)
        this._peakDetectionState = `idle`;
        this._armedTime = 0;
        this._peakValue = 0;
        this._peakTime = 0;
        this._peakDecreaseCount = 0;
        this._prevDeviation = 0;

        // Auto-sensitivity state
        this._recentStepDeviations = [];
        this._recentStepAmplitudes = [];
        this._recentStepIntervals = [];
        this._lastAutoTuneTime = 0;
        this._autoTuneStepCounter = 0;

        // Counters
        this._stepCount = 0;
        this._startTime = null;
        this._isCounting = false;
        this._isPaused = false;
        this._distanceMeters = 0;

        // Activity tracking
        this._stepTimestamps = [];
        this._stepsPerMinuteHistory = [];
        this._activityTimeLabels = [];
        this._lastActivityUpdateTime = 0;

        // Stationary detection (dual-condition)
        this._stationary = {
            isStationary: false,
            lowVarianceStartTime: null,
            lastStepTime: 0,
        };

        // Activity classification
        this._activity = {
            state: `unknown`,
            cadence: 0,
            cadenceBuffer: [],
        };

        // Step length filtering (faster adaptation for hospital small steps)
        this._stepLengthBuffer = [];
        this._useMedianFilter = true;
        this._stepLengthEmaAlpha = 0.55;  // Faster adaptation (was 0.85 = too slow)
        this._lastStepLengthRaw = this._stepLength;
        this._lastStepLengthFiltered = this._stepLength;

        // Amplitude ratio buffer for capping weinbergAmp using pvAmp
        this._ampRatioBuf = [];

        // Weinberg amplitude samples
        this._samplesSinceLastStep = [];
        this._samplesSinceLastStepHead = 0;

        // Gravity estimate
        this._g = { x: 0, y: 0, z: 0 };

        // GPS calibration
        this._gpsCalibrationData = null;
        this._gpsTrack = null;

        // Debug logging (set pedometer.debug = true to enable)
        this._debug = true;
        this._debugLastLog = 0;

        this._updateThresholds();
    }

    // Public getters
    get stepCount() { return this._stepCount; }
    get distance() { return this._distanceMeters; }
    get distanceFormatted() {
        return this.distance >= 1000
            ? `${(this.distance / 1000).toFixed(2)} km`
            : `${this.distance.toFixed(0)} m`;
    }
    get duration() {
        return this._startTime ? Math.floor((Date.now() - this._startTime) / 1000) : 0;
    }
    get durationFormatted() {
        const s = this.duration, m = Math.floor(s / 60);
        return `${String(m).padStart(2, `0`)}:${String(s % 60).padStart(2, `0`)}`;
    }
    get pace() {
        const mins = this.duration / 60;
        return mins > 0 ? Math.round(this._stepCount / mins) : 0;
    }
    get paceFormatted() { return `${this.pace} spm`; }
    get sensitivity() { return this._sensitivity; }
    get stepLength() { return this._stepLength; }
    get strideLength() { return this._stepLength; }
    get isPaused() { return this._isPaused; }
    get activityHistory() {
        return {
            stepsPerMinute: [...this._stepsPerMinuteHistory],
            timeLabels: [...this._activityTimeLabels],
            chartWindow: Pedometer.CONFIG.buffer.chartWindow
        };
    }
    get peakThreshold() { return this._peakThreshold; }
    get minStepIntervalMs() { return this._minStepIntervalMs; }
    get weinbergK() { return this._weinbergK; }
    get lastStepLength() { return this._lastStepLengthRaw; }
    get lastStepLengthFiltered() { return this._lastStepLengthFiltered; }
    get isCalibrating() { return this._gpsCalibrationData !== null; }
    get maxStepLength() { return this._maxStepLength; }
    get minStepLength() { return Pedometer.CONFIG.threshold.minStepLength; }
    get isStationary() { return this._stationary.isStationary; }
    get activityState() { return this._activity.state; }
    get cadence() { return this._activity.cadence; }
    get debug() { return this._debug; }
    get walkingState() { return this._walkingState; }
    get walkSignal() { return this._walkSignal; }
    get medianStepLength() {
        return this._stepLengthBuffer.length === 0
            ? this._stepLength
            : this._computeMedian(this._stepLengthBuffer);
    }
    get medianStrideLength() { return this.medianStepLength; }

    // Public setters
    set sensitivity(v) {
        if ([`low`, `medium`, `high`, `auto`].includes(v)) {
            this._sensitivity = v;
            this._updateThresholds();
        }
    }
    set stepLength(v) {
        if (typeof v === `number` && v > 0.05 && v < 2) this._stepLength = v;
    }
    set strideLength(v) { this.stepLength = v; }
    set peakThreshold(v) {
        if (typeof v === `number` && v > 0) this._peakThreshold = v;
    }
    set minStepIntervalMs(v) {
        if (typeof v === `number` && v > 0) this._minStepIntervalMs = v;
    }
    set weinbergK(v) {
        if (typeof v === `number` && v > 0 && v < 2) this._weinbergK = v;
    }
    set debug(v) { this._debug = !!v; }

    configure(config = {}) {
        if (config.sensitivity !== undefined) this.sensitivity = config.sensitivity;
        if (config.stepLength !== undefined) this.stepLength = config.stepLength;
        if (config.strideLength !== undefined) this.stepLength = config.strideLength;
        if (config.peakThreshold !== undefined) this.peakThreshold = config.peakThreshold;
        if (config.minStepIntervalMs !== undefined) this.minStepIntervalMs = config.minStepIntervalMs;
        if (config.weinbergK !== undefined) this.weinbergK = config.weinbergK;
    }

    // Lifecycle
    async start() {
        const now = Date.now();
        if (!this._startTime) this._startTime = now;
        if (!this._baselineInitStartTime) this._baselineInitStartTime = now;
        this._isCounting = true;
        this._isPaused = false;
        this.dispatchEvent(new CustomEvent(`start`, { detail: this._getEventData() }));
        return true;
    }

    stop() {
        this._isCounting = false;
        this._isPaused = true;
        this.dispatchEvent(new CustomEvent(`stop`, { detail: this._getEventData() }));
    }

    pause() {
        this.stop();
        this.dispatchEvent(new CustomEvent(`pause`, { detail: this._getEventData() }));
    }

    async resume() {
        if (this._listening) return true;
        const result = await this.start();
        if (result) this.dispatchEvent(new CustomEvent(`resume`, { detail: this._getEventData() }));
        return result;
    }

    reset() {
        const wasCounting = this._isCounting;

        this._stepCount = 0;
        this._startTime = wasCounting ? Date.now() : null;
        this._rollingStats = { mean: 0 };
        this._magnitudeRingBuffer = [];
        this._magnitudeRingHead = 0;
        this._baselineMagnitude = 0;
        this._magnitudeVariance = 0;
        this._lastStepTime = 0;
        this._lastSampleTime = 0;
        this._baselineInitStartTime = null;

        // Band-pass filter reset
        this._walkSignalFast = 0;
        this._walkSignalSlow = 0;
        this._walkSignal = 0;
        this._prevWalkSignal = 0;

        // Valley-peak-valley reset
        this._extremaState = `seeking_valley`;
        this._lastValley = { value: 0, time: 0 };
        this._lastPeak = { value: 0, time: 0 };
        this._currentExtrema = { value: 0, time: 0 };
        this._extremaHoldCount = 0;

        // Walking state reset
        this._walkingState = `idle`;
        this._stepCandidates = [];
        this._cooldownEndTime = 0;
        this._lastWalkingStepTime = 0;

        // Rotation gate reset
        this._gPrev = { x: 0, y: 0, z: 0 };
        this._omega = 0;

        this._peakDetectionState = `idle`;
        this._armedTime = 0;
        this._peakValue = 0;
        this._peakTime = 0;
        this._peakDecreaseCount = 0;
        this._prevDeviation = 0;

        this._stepsPerMinuteHistory = [];
        this._activityTimeLabels = [];
        this._stepTimestamps = [];
        this._lastActivityUpdateTime = 0;

        this._distanceMeters = 0;
        this._samplesSinceLastStep = [];
        this._samplesSinceLastStepHead = 0;
        this._g = { x: 0, y: 0, z: 0 };
        this._lastStepLengthRaw = this._stepLength;
        this._lastStepLengthFiltered = this._stepLength;
        this._gpsCalibrationData = null;

        this._recentStepDeviations = [];
        this._recentStepAmplitudes = [];
        this._recentStepIntervals = [];
        this._lastAutoTuneTime = 0;
        this._autoTuneStepCounter = 0;

        this._stationary = { isStationary: false, lowVarianceStartTime: null, lastStepTime: 0 };
        this._activity.state = `unknown`;
        this._activity.cadence = 0;
        this._activity.cadenceBuffer = [];
        this._stepLengthBuffer = [];
        this._ampRatioBuf = [];

        this.dispatchEvent(new CustomEvent(`reset`, { detail: this._getEventData() }));
    }

    dispose() { this.stop(); }

    // GPS Calibration
    startGPSCalibration() {
        this._gpsCalibrationData = {
            amplitudeSum: 0,
            stepCount: 0,
            startDistance: this._distanceMeters,
            startTime: Date.now()
        };
        console.log(`[Pedometer] GPS calibration started`);
        this.dispatchEvent(new CustomEvent(`calibration-start`, { detail: this._getEventData() }));
    }

    finishGPSCalibration(gpsDistance) {
        if (!this._gpsCalibrationData) {
            console.warn(`[Pedometer] No calibration session active`);
            return null;
        }

        const { amplitudeSum, stepCount, startTime } = this._gpsCalibrationData;
        const duration = (Date.now() - startTime) / 1000;

        if (stepCount < 10) {
            console.warn(`[Pedometer] Not enough steps for calibration: ${stepCount}`);
            this._gpsCalibrationData = null;
            return null;
        }
        if (gpsDistance < 5) {
            console.warn(`[Pedometer] GPS distance too short: ${gpsDistance}m`);
            this._gpsCalibrationData = null;
            return null;
        }
        if (amplitudeSum <= 0) {
            console.warn(`[Pedometer] Invalid amplitude sum`);
            this._gpsCalibrationData = null;
            return null;
        }

        const oldK = this._weinbergK;
        let newK = Math.min(1.0, Math.max(0.2, gpsDistance / amplitudeSum));
        this._weinbergK = 0.7 * newK + 0.3 * oldK;

        const result = { oldK, newK, finalK: this._weinbergK, gpsDistance, stepCount, amplitudeSum, duration, avgStepLength: gpsDistance / stepCount };
        console.log(`[Pedometer] GPS calibration complete:`, result);
        this._gpsCalibrationData = null;
        this.dispatchEvent(new CustomEvent(`calibration-complete`, { detail: { ...this._getEventData(), calibration: result } }));
        return result;
    }

    cancelGPSCalibration() {
        if (this._gpsCalibrationData) {
            this._gpsCalibrationData = null;
            console.log(`[Pedometer] GPS calibration cancelled`);
            this.dispatchEvent(new CustomEvent(`calibration-cancel`, { detail: this._getEventData() }));
        }
    }

    updateGPSPosition(position, minSegmentDistance = 30) {
        if (!position || !isFinite(position.latitude) || !isFinite(position.longitude)) return;
        if (position.accuracy && position.accuracy > 20) return;

        if (!this._gpsTrack) {
            this._gpsTrack = {
                lastPosition: position,
                accumulatedGPSDistance: 0,
                accumulatedAmplitudeSum: 0,
                accumulatedSteps: 0
            };
            this.startGPSCalibration();
            return;
        }

        const gpsDist = this._haversineDistance(
            this._gpsTrack.lastPosition.latitude, this._gpsTrack.lastPosition.longitude,
            position.latitude, position.longitude
        );

        if (gpsDist > 0) {
            this._gpsTrack.accumulatedGPSDistance += gpsDist;
            this._gpsTrack.lastPosition = position;
            if (this._gpsCalibrationData) {
                this._gpsTrack.accumulatedAmplitudeSum = this._gpsCalibrationData.amplitudeSum;
                this._gpsTrack.accumulatedSteps = this._gpsCalibrationData.stepCount;
            }
        }

        if (this._gpsTrack.accumulatedGPSDistance >= minSegmentDistance && this._gpsTrack.accumulatedSteps >= 20) {
            const result = this.finishGPSCalibration(this._gpsTrack.accumulatedGPSDistance);
            this._gpsTrack.accumulatedGPSDistance = 0;
            this._gpsTrack.accumulatedAmplitudeSum = 0;
            this._gpsTrack.accumulatedSteps = 0;
            this.startGPSCalibration();
            return result;
        }
        return null;
    }

    _haversineDistance(lat1, lon1, lat2, lon2) {
        const R = 6371000;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    // Accelerometer processing
    processAccelerometer(data) {
        if (!this._isCounting || this._isPaused || !data) return;

        const C = Pedometer.CONFIG;
        const now = Date.now();
        const ax = data.x ?? 0, ay = data.y ?? 0, az = data.z ?? 0;
        if (!isFinite(ax) || !isFinite(ay) || !isFinite(az)) return;

        // Time-based alpha for device independence
        const dt = this._lastSampleTime > 0 ? (now - this._lastSampleTime) : 16;
        this._lastSampleTime = now;
        const dtClamped = Math.max(5, Math.min(100, dt));

        // Estimate gravity
        const gravityAlpha = Math.exp(-dtClamped / C.tau.gravity);
        this._g.x = gravityAlpha * this._g.x + (1 - gravityAlpha) * ax;
        this._g.y = gravityAlpha * this._g.y + (1 - gravityAlpha) * ay;
        this._g.z = gravityAlpha * this._g.z + (1 - gravityAlpha) * az;

        // Rotation gate: compute angular speed of gravity vector (rad/s)
        const dtS = dtClamped / 1000;
        const gx = this._g.x, gy = this._g.y, gz = this._g.z;
        const pgx = this._gPrev.x, pgy = this._gPrev.y, pgz = this._gPrev.z;
        const gMagCurr = Math.sqrt(gx * gx + gy * gy + gz * gz);
        const gMagPrev = Math.sqrt(pgx * pgx + pgy * pgy + pgz * pgz);

        if (gMagCurr > 0.1 && gMagPrev > 0.1 && dtS > 0) {
            const dot = (gx * pgx + gy * pgy + gz * pgz) / (gMagCurr * gMagPrev);
            const clampedDot = Math.max(-1, Math.min(1, dot));
            const angle = Math.acos(clampedDot);
            this._omega = angle / dtS;
        } else {
            this._omega = 0;
        }
        this._gPrev = { x: gx, y: gy, z: gz };

        // Linear acceleration
        const linX = ax - this._g.x, linY = ay - this._g.y, linZ = az - this._g.z;

        // Signed vertical acceleration (for Weinberg amplitude and band-pass filter)
        const gMag = Math.sqrt(this._g.x ** 2 + this._g.y ** 2 + this._g.z ** 2);
        const verticalAccelSigned = gMag > 0.1
            ? (linX * this._g.x + linY * this._g.y + linZ * this._g.z) / gMag
            : 0;

        if (!isFinite(verticalAccelSigned)) return;

        // Band-pass filter: walkSignal = fastEMA - slowEMA (rejects rotation drift)
        const fastAlpha = Math.exp(-dtClamped / C.tau.walkSignalFast);
        const slowAlpha = Math.exp(-dtClamped / C.tau.walkSignalSlow);
        this._walkSignalFast = fastAlpha * this._walkSignalFast + (1 - fastAlpha) * verticalAccelSigned;
        this._walkSignalSlow = slowAlpha * this._walkSignalSlow + (1 - slowAlpha) * verticalAccelSigned;
        this._prevWalkSignal = this._walkSignal;
        this._walkSignal = this._walkSignalFast - this._walkSignalSlow;

        // Also compute linear magnitude for variance/stationary (but NOT for step detection)
        const linMag = Math.sqrt(linX * linX + linY * linY + linZ * linZ);

        // Ring buffer management (use linMag for variance stats, keep signed for Weinberg)
        this._magnitudeRingBuffer.push({ value: linMag, time: now, signed: verticalAccelSigned, walkSig: this._walkSignal });
        const cutoffTime = now - C.buffer.maxSampleWindow;
        while (this._magnitudeRingHead < this._magnitudeRingBuffer.length &&
            this._magnitudeRingBuffer[this._magnitudeRingHead].time < cutoffTime) {
            this._magnitudeRingHead++;
        }
        if (this._magnitudeRingHead > 50) {
            this._magnitudeRingBuffer = this._magnitudeRingBuffer.slice(this._magnitudeRingHead);
            this._magnitudeRingHead = 0;
        }

        this._updateRollingStats(linMag, dtClamped);

        // Weinberg samples stay signed vertical
        this._samplesSinceLastStep.push({ value: verticalAccelSigned, time: now });
        const stepCutoff = now - 2000;
        while (this._samplesSinceLastStepHead < this._samplesSinceLastStep.length &&
            this._samplesSinceLastStep[this._samplesSinceLastStepHead].time < stepCutoff) {
            this._samplesSinceLastStepHead++;
        }
        if (this._samplesSinceLastStepHead > 50) {
            this._samplesSinceLastStep = this._samplesSinceLastStep.slice(this._samplesSinceLastStepHead);
            this._samplesSinceLastStepHead = 0;
        }

        this._updateStationary(linMag, now);

        // Use valley-peak-valley detection on band-pass filtered signal
        if (!this._stationary.isStationary) {
            this._detectStepVPV(this._walkSignal, now, dtClamped, linMag);
        }

        if (!this._lastActivityUpdateTime || (now - this._lastActivityUpdateTime) >= 500) {
            this._updateActivityData();
            this._lastActivityUpdateTime = now;
        }
    }

    simulateStep() {
        const now = Date.now();
        if ((now - this._lastStepTime) < this._minStepIntervalMs) return;

        this._updateCadence(now);
        this._lastStepTime = now;
        this._stepCount++;

        // Keep system consistent with a real step
        this._stationary.lastStepTime = now;
        this._stepTimestamps.push(now);
        this._updateActivityState();

        // Distance with current estimate (clamped)
        const fallbackStep = Math.min(this._stepLength, this._maxStepLength);
        this._distanceMeters += fallbackStep;
        this._lastStepLengthRaw = fallbackStep;
        this._lastStepLengthFiltered = fallbackStep;

        this._emitStep(now);
    }

    // Private methods
    _updateThresholds() {
        const preset = Pedometer.CONFIG.sensitivity[this._sensitivity];
        if (preset) {
            this._peakThreshold = preset.peakThreshold;
            this._minStepIntervalMs = preset.minStepIntervalMs;
            this._maxStepLength = preset.maxStepLength;
        }
    }

    /**
     * Valley-Peak-Valley step detection (MDPI paper approach)
     * Detects steps as: valley → peak → valley pattern on band-pass filtered signal.
     * Uses walking state gate to reject isolated bumps.
     * Implements rotation gate and asymmetric thresholds.
     */
    _detectStepVPV(walkSig, now, dt, linMag) {
        const C = Pedometer.CONFIG;

        // Initialization warmup
        if (!this._baselineInitStartTime) this._baselineInitStartTime = now;
        const initDuration = now - this._baselineInitStartTime;
        if (initDuration < C.init.baseline + C.init.varianceWarmup) return;

        // Rotation gate: if rotating fast with low linear acceleration, it's turning, not walking
        if (this._omega > C.walkingGate.rotationOmegaRad && linMag < C.walkingGate.rotationLinMagMax) {
            this._walkingState = `cooldown`;
            this._cooldownEndTime = now + C.walkingGate.cooldownDuration;
            this._extremaState = `seeking_valley`;
            this._stepCandidates = [];
            return;
        }

        // Cooldown check
        if (this._walkingState === `cooldown` && now < this._cooldownEndTime) return;
        if (this._walkingState === `cooldown` && now >= this._cooldownEndTime) {
            this._walkingState = `idle`;
        }

        // Walking timeout: drop to idle if no steps for too long
        if (this._walkingState === `walking` && this._lastWalkingStepTime > 0) {
            if ((now - this._lastWalkingStepTime) > C.walkingGate.walkingTimeout) {
                this._walkingState = `idle`;
                this._extremaState = `seeking_valley`;
                this.dispatchEvent(new CustomEvent(`walking-stopped`, { detail: this._getEventData() }));
            }
        }

        // Clean old step candidates (keep only within candidate window)
        const candidateCutoff = now - C.walkingGate.candidateWindow;
        while (this._stepCandidates.length > 0 && this._stepCandidates[0].t < candidateCutoff) {
            this._stepCandidates.shift();
        }

        // Adaptive thresholds based on walking state (MDPI paper: asymmetric thresholds)
        // In idle: use candidateThresholdFactor (higher bar to become a candidate)
        // In walking: use walkingThresholdFactor (lower bar, rhythm established)
        const thresholdFactor = this._walkingState === `walking`
            ? C.walkingGate.walkingThresholdFactor
            : C.walkingGate.candidateThresholdFactor;
        const peakThr = this._peakThreshold * thresholdFactor; // Peak must exceed this (positive)
        const valleyThr = this._peakThreshold * thresholdFactor * C.vpv.valleyRatio; // Valley must be below -valleyThr

        // Debug logging
        // if (this._debug && (!this._debugLastLog || (now - this._debugLastLog) > 250)) {
        //     this._debugLastLog = now;
        //     console.log(`[Pedometer]`, {
        //         walkSig: walkSig.toFixed(3),
        //         state: this._extremaState,
        //         walkingState: this._walkingState,
        //         peakThr: peakThr.toFixed(3),
        //         valleyThr: (-valleyThr).toFixed(3),
        //         omega: this._omega.toFixed(2),
        //         candidates: this._stepCandidates.length,
        //         valley: this._lastValley.value.toFixed(3),
        //         peak: this._lastPeak.value.toFixed(3)
        //     });
        // }

        // Detect zero-crossings and extrema
        // Note: buffer[length-1] = current(N), buffer[length-2] = prev(N-1), buffer[length-3] = N-2
        const isIncreasing = walkSig > this._prevWalkSignal;
        const isDecreasing = walkSig < this._prevWalkSignal;
        const prevPrevWalkSig = this._magnitudeRingBuffer.length > 3
            ? this._magnitudeRingBuffer[this._magnitudeRingBuffer.length - 3]?.walkSig ?? 0
            : 0;
        const wasIncreasing = this._prevWalkSignal > prevPrevWalkSig;
        const wasDecreasing = this._prevWalkSignal < prevPrevWalkSig;

        // State machine for valley-peak-valley detection with asymmetric thresholds
        if (this._extremaState === `seeking_valley`) {
            // Look for a local minimum (was decreasing, now increasing or flat)
            // Valley must be below -valleyThr (asymmetric: valley has its own threshold)
            if (wasDecreasing && (isIncreasing || Math.abs(walkSig - this._prevWalkSignal) < 0.01)) {
                // Potential valley found - must be negative enough
                if (this._prevWalkSignal < -valleyThr) {
                    this._lastValley = { value: this._prevWalkSignal, time: now - dt };
                    this._extremaState = `seeking_peak`;
                    this._extremaHoldCount = 0;
                }
            }
            // Also accept starting from a very low point
            else if (walkSig < -valleyThr * 1.2) {
                this._lastValley = { value: walkSig, time: now };
                this._extremaState = `seeking_peak`;
                this._extremaHoldCount = 0;
            }
        }
        else if (this._extremaState === `seeking_peak`) {
            // Look for a local maximum (was increasing, now decreasing)
            if (wasIncreasing && isDecreasing) {
                const peakValue = this._prevWalkSignal;
                const peakValleyAmplitude = peakValue - this._lastValley.value;
                const timeSinceValley = now - this._lastValley.time;

                // Validate peak with asymmetric thresholds:
                // 1) Peak must be above +peakThr (positive)
                // 2) Valley must have been below -valleyThr (already checked)
                // 3) Peak-valley amplitude must exceed delta threshold
                const deltaThreshold = peakThr + valleyThr; // Total swing required
                if (peakValue > peakThr &&
                    peakValleyAmplitude > deltaThreshold &&
                    timeSinceValley >= C.vpv.minValleyToPeakMs &&
                    timeSinceValley <= C.vpv.maxValleyToPeakMs) {
                    this._lastPeak = { value: peakValue, time: now - dt };
                    this._extremaState = `confirming_step`;
                    this._extremaHoldCount = 0;
                }
                // Peak too small or timing wrong - reset to seeking valley
                else if (timeSinceValley > C.vpv.maxValleyToPeakMs) {
                    this._extremaState = `seeking_valley`;
                }
            }
            // Timeout - no peak found
            else if ((now - this._lastValley.time) > C.vpv.maxValleyToPeakMs) {
                this._extremaState = `seeking_valley`;
            }
            // Update valley if we go lower
            else if (walkSig < this._lastValley.value) {
                this._lastValley = { value: walkSig, time: now };
            }
        }
        else if (this._extremaState === `confirming_step`) {
            // Look for the second valley (was decreasing, now increasing)
            if (wasDecreasing && (isIncreasing || Math.abs(walkSig - this._prevWalkSignal) < 0.01)) {
                const secondValleyValue = this._prevWalkSignal;
                const peakToValleyAmplitude = this._lastPeak.value - secondValleyValue;
                const timeSincePeak = now - this._lastPeak.time;
                const totalStepDuration = now - this._lastValley.time;

                // Validate second valley with asymmetric threshold
                // Second valley should be below -valleyThr * 0.5 (slightly relaxed)
                if (secondValleyValue < -valleyThr * 0.5 &&
                    peakToValleyAmplitude > (peakThr + valleyThr) * 0.5 &&
                    timeSincePeak >= C.vpv.minPeakToValleyMs &&
                    timeSincePeak <= C.vpv.maxPeakToValleyMs &&
                    totalStepDuration >= this._minStepIntervalMs &&
                    totalStepDuration <= C.vpv.maxStepDurationMs) {

                    // Valid valley-peak-valley pattern detected!
                    // Capture amplitude data for proper replay
                    const pvAmp = this._lastPeak.value - this._lastValley.value;
                    this._handleStepCandidate(now, pvAmp);

                    // Prepare for next step (use this valley as start)
                    this._lastValley = { value: secondValleyValue, time: now - dt };
                    this._extremaState = `seeking_peak`;
                }
                // Invalid - reset
                else if (timeSincePeak > C.vpv.maxPeakToValleyMs || totalStepDuration > C.vpv.maxStepDurationMs) {
                    this._extremaState = `seeking_valley`;
                }
            }
            // Timeout - no second valley found
            else if ((now - this._lastPeak.time) > C.vpv.maxPeakToValleyMs) {
                this._extremaState = `seeking_valley`;
            }
            // Update peak if we go higher (double-peak handling)
            else if (walkSig > this._lastPeak.value) {
                this._lastPeak = { value: walkSig, time: now };
            }
        }
    }

    /**
     * Handle a step candidate with walking state gate.
     * Now stores amplitude data with candidates for proper replay.
     * Requires rhythm consistency (interval CV) to confirm walking.
     */
    _handleStepCandidate(now, pvAmp) {
        const C = Pedometer.CONFIG;

        // Min interval check
        if ((now - this._lastStepTime) < this._minStepIntervalMs) return;

        // Compute Weinberg amplitude from current samples using ROBUST RANGE (p90-p10)
        // This prevents single spikes (rotation, arm swing) from inflating amplitude
        const stepSamples = this._samplesSinceLastStep;
        const validSamples = stepSamples.length - this._samplesSinceLastStepHead;
        let weinbergAmp = 0;
        let samplesCopy = [];
        if (validSamples >= 5) {
            const values = [];
            for (let i = this._samplesSinceLastStepHead; i < stepSamples.length; i++) {
                const v = stepSamples[i].value;
                values.push(v);
                samplesCopy.push({ value: v, time: stepSamples[i].time });
            }
            // Use robust range instead of max-min to reject spikes
            weinbergAmp = this._robustRange(values);
        }

        if (this._walkingState === `idle`) {
            // In idle: add to candidates with amplitude data, check for walking confirmation
            this._stepCandidates.push({
                t: now,
                pvAmp: pvAmp,
                weinbergAmp: weinbergAmp,
                samples: samplesCopy
            });

            if (this._stepCandidates.length >= C.walkingGate.confirmSteps) {
                // Check if candidates are within confirmation window
                const oldest = this._stepCandidates[0].t;
                const newest = this._stepCandidates[this._stepCandidates.length - 1].t;
                if ((newest - oldest) <= C.walkingGate.confirmWindow) {
                    // Check rhythm consistency (interval CV)
                    if (this._checkRhythmConsistency()) {
                        // Transition to walking!
                        this._walkingState = `walking`;

                        // Count all confirmed candidates as steps using their stored data
                        for (let i = 0; i < this._stepCandidates.length; i++) {
                            const candidate = this._stepCandidates[i];
                            if (candidate.t !== now) {
                                this._confirmStepFromCandidate(candidate);
                            }
                        }
                        this._stepCandidates = [];

                        // Count current step with current data
                        this._confirmStep(now, pvAmp, weinbergAmp);
                        this._lastWalkingStepTime = now;
                        this.dispatchEvent(new CustomEvent(`walking-started`, { detail: this._getEventData() }));
                    }
                }
            }
        }
        else if (this._walkingState === `walking`) {
            // In walking: count step directly (with lower threshold already applied)
            this._confirmStep(now, pvAmp, weinbergAmp);
            this._lastWalkingStepTime = now;
        }
    }

    /**
     * Check rhythm consistency using coefficient of variation of intervals.
     * Returns true if intervals are consistent enough to confirm walking.
     */
    _checkRhythmConsistency() {
        const C = Pedometer.CONFIG;
        if (this._stepCandidates.length < 2) return true; // Not enough data

        // Compute intervals between candidates
        const intervals = [];
        for (let i = 1; i < this._stepCandidates.length; i++) {
            intervals.push(this._stepCandidates[i].t - this._stepCandidates[i - 1].t);
        }

        if (intervals.length === 0) return true;

        // Compute mean and standard deviation
        const mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
        if (mean === 0) return false;

        const variance = intervals.reduce((sum, val) => sum + (val - mean) ** 2, 0) / intervals.length;
        const stdDev = Math.sqrt(variance);
        const cv = stdDev / mean; // Coefficient of variation

        // Check cadence is within plausible bounds (60-200 spm → 300-1000ms interval)
        const cadenceSpm = mean > 0 ? 60000 / mean : 0;
        const plausibleCadence = cadenceSpm >= C.walkingGate.minCadenceSpm && cadenceSpm <= C.walkingGate.maxCadenceSpm;

        return cv <= C.walkingGate.maxIntervalCV && plausibleCadence;
    }

    /**
     * Confirm a step from stored candidate data (for replaying older candidates)
     */
    _confirmStepFromCandidate(candidate) {
        const C = Pedometer.CONFIG;
        const now = candidate.t;

        // Get previous step time BEFORE updating _lastStepTime
        const prevStepTime = this._lastStepTime;

        this._stepCount++;
        this._updateCadence(now);
        this._lastStepTime = now;
        this._updateActivityState();
        this._stationary.lastStepTime = now;

        // Use stored Weinberg amplitude for step length
        if (candidate.weinbergAmp > 0 && candidate.samples.length >= 5) {
            const stepLen = this._computeStepLength(
                candidate.weinbergAmp,
                candidate.pvAmp,
                now,
                prevStepTime,
                C
            );

            // Median filter
            this._stepLengthBuffer.push(stepLen);
            if (this._stepLengthBuffer.length > C.buffer.stepLength) this._stepLengthBuffer.shift();

            const filteredStepLen = this._useMedianFilter && this._stepLengthBuffer.length >= 3
                ? this._computeMedian(this._stepLengthBuffer)
                : stepLen;

            this._distanceMeters += filteredStepLen;
            this._lastStepLengthRaw = stepLen;
            this._lastStepLengthFiltered = filteredStepLen;
            this._stepLength = this._stepLengthEmaAlpha * this._stepLength + (1 - this._stepLengthEmaAlpha) * filteredStepLen;

            if (this._gpsCalibrationData) {
                this._gpsCalibrationData.amplitudeSum += Math.pow(candidate.weinbergAmp, 0.25);
                this._gpsCalibrationData.stepCount++;
            }
        } else {
            // Fallback step length
            const fallbackStep = Math.min(this._stepLength, this._maxStepLength);
            this._distanceMeters += fallbackStep;
            this._lastStepLengthRaw = fallbackStep;
            this._lastStepLengthFiltered = fallbackStep;
        }

        this._stepTimestamps.push(now);
        this._emitStep(now);
    }

    /**
     * Compute step length using dual-amplitude fusion, ratio capping, and instant cadence scaling.
     * Addresses: Weinberg amplitude inflation from rotation/arm swing, and ^0.25 limitation for slow gait.
     *
     * Key fixes:
     * - Robust amplitude (p90-p10) computed upstream instead of max-min
     * - Cap weinbergAmp using pvAmp with auto-learned ratio (prevents rotation contamination)
     * - Omega penalty when phone is rotating (rotation makes vertical projection unreliable)
     * - Aggressive cadence scaling for slow/shuffling gait
     */
    _computeStepLength(weinbergAmp, pvAmp, timestamp, prevStepTime, C) {
        const baseK = this._weinbergK;

        // 1) Instant cadence (not buffered median) for faster response to speed changes
        const intervalMs = (prevStepTime > 0) ? (timestamp - prevStepTime) : 700;
        const cadenceInst = intervalMs > 0 ? (60000 / intervalMs) : 80;

        // 2) Adaptive exponent: use 0.40 for very slow cadence (more sensitive to amplitude)
        const exponent = cadenceInst < 65 ? 0.40 : 0.25;

        // 3) Cap weinbergAmp using pvAmp with auto-learned ratio
        // This is THE key fix for logs showing pvAmp~0.8 but weinbergAmp~5.0 (rotation artifact)
        let ampCapped = weinbergAmp;
        if (pvAmp > 0.3 && weinbergAmp > 0.3) {
            // Learn ratio during normal walking
            if (this._walkingState === `walking` && pvAmp > 0.5 && weinbergAmp > 0.5) {
                const ratio = weinbergAmp / pvAmp;
                this._ampRatioBuf.push(ratio);
                if (this._ampRatioBuf.length > 25) this._ampRatioBuf.shift();
            }

            // Compute ratio cap: median ratio * 1.2 (allow some headroom)
            const ratioMed = this._ampRatioBuf.length >= 3
                ? this._computeMedian(this._ampRatioBuf)
                : 2.0; // Default if not enough data
            const ratioCap = ratioMed * 1.2;

            // Cap weinbergAmp: can't be more than ratioCap * pvAmp
            const ampMax = pvAmp * ratioCap;
            if (weinbergAmp > ampMax) {
                ampCapped = ampMax;
            }
        }

        // 4) Omega penalty: when phone is rotating, vertical projection is unreliable
        // Reduce amplitude contribution when omega is high
        let omegaPenalty = 1.0;
        if (this._omega > 1.5) {
            // omega > 1.5 rad/s (~86 deg/s) = significant rotation
            omegaPenalty = this._omega > 2.5 ? 0.6 : 0.8;
            ampCapped *= omegaPenalty;
        }

        // 5) Compute step length from CAPPED amplitude
        const ampRaw = Math.max(0.001, ampCapped);
        const lenRaw = baseK * Math.pow(ampRaw, exponent);

        // 6) Compute step length from BAND-PASS amplitude (pvAmp - less rotation artifact)
        const ampBP = Math.max(0.001, pvAmp || 0);
        const Kbp = baseK * C.stepLen.bpKScale;
        const lenBP = Kbp * Math.pow(ampBP, exponent);

        // 7) Conservative fusion: take minimum (overestimation is the problem)
        let stepLen = (pvAmp > 0) ? Math.min(lenRaw, lenBP) : lenRaw;

        // 8) Aggressive cadence scaling: 85 spm -> 1.0, below -> power curve shrink
        let cadScale = 1.0;
        if (cadenceInst < 85) {
            const x = cadenceInst / 85;
            cadScale = Math.max(0.40, Math.pow(x, 1.6));
            stepLen *= cadScale;
        }

        // 9) Clear median buffer when entering very slow mode (prevents lag from old big steps)
        if (cadenceInst < 65 && this._stepLengthBuffer.length > 0) {
            this._stepLengthBuffer = [];
        }

        // 10) Clamp
        stepLen = Math.min(this._maxStepLength, Math.max(C.threshold.minStepLength, stepLen));

        // Debug logging for tuning (per-step)
        if (this._debug) {
            console.log(`[Pedometer stepLen]`, {
                cadenceInst: Math.round(cadenceInst),
                pvAmp: (pvAmp || 0).toFixed(3),
                weinbergAmp: weinbergAmp.toFixed(3),
                ampCapped: ampCapped.toFixed(3),
                omegaPen: omegaPenalty.toFixed(2),
                lenRaw: lenRaw.toFixed(3),
                lenBP: lenBP.toFixed(3),
                cadScale: cadScale.toFixed(2),
                stepLen: stepLen.toFixed(3),
                exponent
            });
        }

        return stepLen;
    }

    /**
     * Confirm and count a step with provided amplitude data.
     * Uses dual-amplitude fusion and instant cadence for accurate step length.
     */
    _confirmStep(timestamp, pvAmp, weinbergAmp) {
        const C = Pedometer.CONFIG;
        const now = timestamp;

        // Get previous step time BEFORE updating _lastStepTime
        const prevStepTime = this._lastStepTime;

        this._stepCount++;
        this._updateCadence(now);
        this._lastStepTime = now;
        this._updateActivityState();
        this._stationary.lastStepTime = now;

        // Use provided Weinberg amplitude or compute from samples using robust range
        let amplitude = weinbergAmp;
        if (!amplitude || amplitude <= 0) {
            const stepSamples = this._samplesSinceLastStep;
            const validStepSamples = stepSamples.length - this._samplesSinceLastStepHead;
            if (validStepSamples >= 5) {
                const values = [];
                for (let i = this._samplesSinceLastStepHead; i < stepSamples.length; i++) {
                    values.push(stepSamples[i].value);
                }
                // Use robust range (p90-p10) to reject spike contamination
                amplitude = this._robustRange(values);
            }
        }

        if (amplitude > 0) {
            const stepLen = this._computeStepLength(amplitude, pvAmp, now, prevStepTime, C);

            // Median filter
            this._stepLengthBuffer.push(stepLen);
            if (this._stepLengthBuffer.length > C.buffer.stepLength) this._stepLengthBuffer.shift();

            const filteredStepLen = this._useMedianFilter && this._stepLengthBuffer.length >= 3
                ? this._computeMedian(this._stepLengthBuffer)
                : stepLen;

            this._distanceMeters += filteredStepLen;
            this._lastStepLengthRaw = stepLen;
            this._lastStepLengthFiltered = filteredStepLen;
            this._stepLength = this._stepLengthEmaAlpha * this._stepLength + (1 - this._stepLengthEmaAlpha) * filteredStepLen;

            if (this._gpsCalibrationData) {
                this._gpsCalibrationData.amplitudeSum += Math.pow(amplitude, 0.25);
                this._gpsCalibrationData.stepCount++;
            }

            // Only auto-tune when in confirmed walking state
            if (this._walkingState === `walking` && pvAmp > 0) {
                this._trackStepForAutoTune(pvAmp, amplitude, now);
            }
        } else {
            const fallbackStep = Math.min(this._stepLength, this._maxStepLength);
            this._distanceMeters += fallbackStep;
            this._lastStepLengthRaw = fallbackStep;
            this._lastStepLengthFiltered = fallbackStep;
        }

        this._samplesSinceLastStep = [];
        this._samplesSinceLastStepHead = 0;
        this._stepTimestamps.push(now);
        this._emitStep(now);
    }

    // Legacy step detection (kept for reference/fallback)
    _detectStep(magnitude, now, dt) {
        const C = Pedometer.CONFIG;
        const buffer = this._magnitudeRingBuffer;
        const validSampleCount = buffer.length - this._magnitudeRingHead;

        // Time-based initialization
        if (!this._baselineInitStartTime) this._baselineInitStartTime = now;
        const initDuration = now - this._baselineInitStartTime;

        if (initDuration < C.init.baseline) {
            if (validSampleCount >= 5) {
                let sum = 0;
                for (let i = this._magnitudeRingHead; i < buffer.length; i++) sum += buffer[i].value;
                this._baselineMagnitude = sum / validSampleCount;
            }
            return;
        }

        // Variance warmup
        if (initDuration < C.init.baseline + C.init.varianceWarmup) return;

        // Update baseline only in idle state (freeze during peak detection)
        if (this._peakDetectionState === `idle`) {
            const baselineAlpha = Math.exp(-dt / C.tau.baseline);
            this._baselineMagnitude = baselineAlpha * this._baselineMagnitude + (1 - baselineAlpha) * magnitude;
        }

        const deviation = Math.abs(magnitude - this._baselineMagnitude);

        // Debug logging (enable with pedometer.debug = true)
        if (this._debug && (!this._debugLastLog || (now - this._debugLastLog) > 250)) {
            this._debugLastLog = now;
            const gMag = Math.sqrt(this._g.x ** 2 + this._g.y ** 2 + this._g.z ** 2);
            const stdDev = Math.sqrt(this._magnitudeVariance);
            console.log(`[Pedometer]`, {
                gMag: gMag.toFixed(2),
                vAbs: magnitude.toFixed(3),
                base: this._baselineMagnitude.toFixed(3),
                dev: deviation.toFixed(3),
                std: stdDev.toFixed(3),
                thr: this._peakThreshold.toFixed(2),
                state: this._peakDetectionState
            });
        }

        // Minimum variance gate: reject sensor noise when too quiet
        if (this._magnitudeVariance < C.threshold.minVariance) return;

        // Use fixed threshold (dynamic variance scaling was raising threshold during walking)
        const armThreshold = this._peakThreshold * C.hysteresis.lowFactor;
        const triggerThreshold = this._peakThreshold * C.hysteresis.highFactor;

        const isDeviationIncreasing = deviation > this._prevDeviation;
        const isDeviationDecreasing = deviation < this._prevDeviation;

        // Timeout checks - return early to prevent immediate re-arm
        if (this._peakDetectionState === `armed` && (now - this._armedTime) > C.hysteresis.armTimeout) {
            this._peakDetectionState = `idle`;
            this._peakDecreaseCount = 0;
            this._prevDeviation = deviation;
            return;
        }
        if (this._peakDetectionState === `peak` && (now - this._peakTime) > C.hysteresis.peakTimeout) {
            this._peakDetectionState = `idle`;
            this._peakDecreaseCount = 0;
            this._prevDeviation = deviation;
            return;
        }

        // State machine
        let triggerStep = false;

        if (this._peakDetectionState === `idle`) {
            if (deviation > armThreshold && isDeviationIncreasing) {
                this._peakDetectionState = `armed`;
                this._armedTime = now;
                this._peakValue = deviation;
                this._peakTime = now;
            }
        } else if (this._peakDetectionState === `armed`) {
            if (deviation > this._peakValue) {
                this._peakValue = deviation;
                this._peakTime = now;
                this._peakDecreaseCount = 0;
                if (deviation > triggerThreshold) this._peakDetectionState = `peak`;
            } else if (isDeviationDecreasing) {
                // Don't drop immediately; allow small local dips/plateaus
                this._peakDecreaseCount++;
                // Cancel only if it keeps dropping or falls back under armThreshold
                if (this._peakDecreaseCount >= 2 || deviation < armThreshold) {
                    this._peakDetectionState = `idle`;
                    this._peakDecreaseCount = 0;
                }
            } else {
                this._peakDecreaseCount = 0;
            }
        } else if (this._peakDetectionState === `peak`) {
            if (deviation < this._peakValue - C.hysteresis.tolerance) {
                this._peakDecreaseCount++;
                if (this._peakDecreaseCount >= C.hysteresis.confirmSamples) {
                    if ((now - this._lastStepTime) > this._minStepIntervalMs) triggerStep = true;
                    this._peakDetectionState = `idle`;
                    this._peakDecreaseCount = 0;
                }
            } else {
                if (deviation > this._peakValue) {
                    this._peakValue = deviation;
                    this._peakTime = now;
                }
                this._peakDecreaseCount = 0;
            }
        }

        this._prevDeviation = deviation;

        if (triggerStep) {
            this._stepCount++;
            this._updateCadence(now);
            this._lastStepTime = now;
            this._updateActivityState();
            this._stationary.lastStepTime = now;

            // Weinberg step length estimation (using robust range)
            const stepSamples = this._samplesSinceLastStep;
            const validStepSamples = stepSamples.length - this._samplesSinceLastStepHead;

            if (validStepSamples >= 5) {
                const values = [];
                for (let i = this._samplesSinceLastStepHead; i < stepSamples.length; i++) {
                    values.push(stepSamples[i].value);
                }
                // Use robust range (p90-p10) instead of spike-sensitive max-min
                const amplitude = this._robustRange(values);
                let stepLen = this._weinbergK * Math.pow(amplitude, 0.25);

                // Cadence correction
                stepLen *= C.cadenceFactors[this._activity.state] ?? 1.0;

                // Clamp
                stepLen = Math.min(this._maxStepLength, Math.max(C.threshold.minStepLength, stepLen));

                // Median filter
                this._stepLengthBuffer.push(stepLen);
                if (this._stepLengthBuffer.length > C.buffer.stepLength) this._stepLengthBuffer.shift();

                const filteredStepLen = this._useMedianFilter && this._stepLengthBuffer.length >= 3
                    ? this._computeMedian(this._stepLengthBuffer)
                    : stepLen;

                this._distanceMeters += filteredStepLen;
                this._lastStepLengthRaw = stepLen;
                this._lastStepLengthFiltered = filteredStepLen;
                this._stepLength = this._stepLengthEmaAlpha * this._stepLength + (1 - this._stepLengthEmaAlpha) * filteredStepLen;

                if (this._gpsCalibrationData) {
                    this._gpsCalibrationData.amplitudeSum += Math.pow(amplitude, 0.25);
                    this._gpsCalibrationData.stepCount++;
                }

                this._trackStepForAutoTune(deviation, amplitude, now);
            } else {
                const fallbackStep = Math.min(this._stepLength, this._maxStepLength);
                this._distanceMeters += fallbackStep;
                this._lastStepLengthRaw = fallbackStep;
                this._lastStepLengthFiltered = fallbackStep;
            }

            this._samplesSinceLastStep = [];
            this._samplesSinceLastStepHead = 0;
            this._stepTimestamps.push(now);
            this._emitStep(now);
        }
    }

    _updateActivityData() {
        if (!this._startTime) return;
        const C = Pedometer.CONFIG;
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - this._startTime) / 1000);

        const oneMinuteAgo = now - 60000;
        while (this._stepTimestamps.length > 0 && this._stepTimestamps[0] < oneMinuteAgo) {
            this._stepTimestamps.shift();
        }

        this._stepsPerMinuteHistory.push(this._stepTimestamps.length);
        this._activityTimeLabels.push(elapsedSeconds);

        if (this._stepsPerMinuteHistory.length > C.buffer.chartWindow) {
            this._stepsPerMinuteHistory.shift();
            this._activityTimeLabels.shift();
        }
    }

    _trackStepForAutoTune(deviation, amplitude, timestamp) {
        if (this._sensitivity !== `auto`) return;
        const C = Pedometer.CONFIG;

        this._recentStepDeviations.push(deviation);
        if (this._recentStepDeviations.length > C.buffer.autoTune) this._recentStepDeviations.shift();

        this._recentStepAmplitudes.push(amplitude);
        if (this._recentStepAmplitudes.length > C.buffer.autoTune) this._recentStepAmplitudes.shift();

        if (this._lastAutoTuneTime > 0) {
            const interval = timestamp - this._lastAutoTuneTime;
            this._recentStepIntervals.push(interval);
            if (this._recentStepIntervals.length > C.buffer.autoTune) this._recentStepIntervals.shift();
        }
        this._lastAutoTuneTime = timestamp;

        // Auto-tune every 10 steps (counter-based to avoid repeated triggers)
        this._autoTuneStepCounter++;
        if (this._autoTuneStepCounter % 10 === 0) {
            this._performAutoTune();
        }
    }

    _performAutoTune() {
        const devs = this._recentStepDeviations;
        const intervals = this._recentStepIntervals;
        if (devs.length < 5) return;

        const sortedDevs = [...devs].sort((a, b) => a - b);
        const medianDev = sortedDevs[Math.floor(sortedDevs.length / 2)];
        // Floor at 0.15 for hospital indoor small steps
        let targetThreshold = Math.min(2.0, Math.max(0.15, medianDev * 0.65));
        this._peakThreshold = 0.85 * this._peakThreshold + 0.15 * targetThreshold;

        if (intervals.length >= 5) {
            const sortedIntervals = [...intervals].sort((a, b) => a - b);
            const medianInterval = sortedIntervals[Math.floor(sortedIntervals.length / 2)];
            // Max 750ms for very slow hospital walking
            let targetInterval = Math.min(750, Math.max(180, medianInterval * 0.8));
            this._minStepIntervalMs = 0.85 * this._minStepIntervalMs + 0.15 * targetInterval;
        }
    }

    _updateRollingStats(value, dt) {
        const alpha = Math.exp(-dt / Pedometer.CONFIG.tau.variance);
        this._rollingStats.mean = alpha * this._rollingStats.mean + (1 - alpha) * value;
        const meanDeviation = value - this._rollingStats.mean;
        this._magnitudeVariance = alpha * this._magnitudeVariance + (1 - alpha) * (meanDeviation * meanDeviation);
    }

    _updateStationary(_magnitude, now) {
        const C = Pedometer.CONFIG.stationary;
        const wasStationary = this._stationary.isStationary;
        const lowVariance = this._magnitudeVariance < C.variance;
        const timeSinceLastStep = this._stationary.lastStepTime > 0 ? (now - this._stationary.lastStepTime) : Infinity;
        const noRecentSteps = timeSinceLastStep > C.minNoStepDuration;

        if (lowVariance) {
            if (!this._stationary.lowVarianceStartTime) this._stationary.lowVarianceStartTime = now;
        } else {
            this._stationary.lowVarianceStartTime = null;
        }

        const lowVarianceDuration = this._stationary.lowVarianceStartTime ? (now - this._stationary.lowVarianceStartTime) : 0;
        const sustainedLowVariance = lowVarianceDuration >= C.minLowVarianceDuration;
        // Add mean-motion requirement so walking never becomes "stationary"
        const meanMotion = this._rollingStats.mean;
        const meanQuiet = meanMotion < (this._peakThreshold * 0.25); // scales with sensitivity
        const shouldBeStationary = sustainedLowVariance && noRecentSteps && meanQuiet;

        if (shouldBeStationary && !wasStationary) {
            this._stationary.isStationary = true;
            this._activity.state = `stationary`;
            // Reset walking state when becoming stationary
            this._walkingState = `idle`;
            this._stepCandidates = [];
            this._extremaState = `seeking_valley`;
            this._lastWalkingStepTime = 0;
            this.dispatchEvent(new CustomEvent(`stationary`, { detail: { ...this._getEventData(), isStationary: true } }));
        } else if (!shouldBeStationary && wasStationary) {
            this._stationary.isStationary = false;
            this.dispatchEvent(new CustomEvent(`moving`, { detail: { ...this._getEventData(), isStationary: false } }));
        }
    }

    _updateCadence(timestamp) {
        const C = Pedometer.CONFIG;
        if (this._lastStepTime > 0) {
            const interval = timestamp - this._lastStepTime;
            if (interval >= 200 && interval <= 3000) {
                this._activity.cadenceBuffer.push(interval);
                if (this._activity.cadenceBuffer.length > C.buffer.cadence) this._activity.cadenceBuffer.shift();
            }
        }
        if (this._activity.cadenceBuffer.length >= 2) {
            const medianInterval = this._computeMedian(this._activity.cadenceBuffer);
            this._activity.cadence = Math.round(60000 / medianInterval);
        }
    }

    _updateActivityState() {
        if (this._stationary.isStationary) {
            this._activity.state = `stationary`;
            return;
        }
        const cadence = this._activity.cadence;
        const T = Pedometer.CONFIG.cadenceThresholds;
        if (cadence <= 0) this._activity.state = `unknown`;
        else if (cadence < T.verySlow) this._activity.state = `verySlow`;
        else if (cadence < T.slow) this._activity.state = `slow`;
        else if (cadence < T.normal) this._activity.state = `normal`;
        else this._activity.state = `fast`;
    }

    _computeMedian(arr) {
        if (arr.length === 0) return 0;
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    }

    /**
     * Compute percentile from a sorted array.
     * @param {number[]} sorted - Pre-sorted array
     * @param {number} p - Percentile (0-1)
     */
    _percentile(sorted, p) {
        if (sorted.length === 0) return 0;
        const i = (sorted.length - 1) * p;
        const lo = Math.floor(i), hi = Math.ceil(i);
        if (lo === hi) return sorted[lo];
        return sorted[lo] * (hi - i) + sorted[hi] * (i - lo);
    }

    /**
     * Compute robust range (p90 - p10) instead of spike-sensitive max-min.
     * This prevents single spikes from inflating amplitude.
     * @param {number[]} values - Array of sample values
     * @param {number} pLo - Low percentile (default 0.10)
     * @param {number} pHi - High percentile (default 0.90)
     */
    _robustRange(values, pLo = 0.10, pHi = 0.90) {
        if (values.length < 5) return 0;
        const sorted = [...values].sort((a, b) => a - b);
        return Math.max(0, this._percentile(sorted, pHi) - this._percentile(sorted, pLo));
    }

    _getEventData() {
        return {
            stepCount: this._stepCount,
            distance: this.distance,
            distanceFormatted: this.distanceFormatted,
            pace: this.pace,
            paceFormatted: this.paceFormatted,
            duration: this.duration,
            durationFormatted: this.durationFormatted,
            stepLength: this._stepLength,
            strideLength: this._stepLength,
            lastStepLength: this._lastStepLengthRaw,
            lastStepLengthFiltered: this._lastStepLengthFiltered,
            medianStepLength: this.medianStepLength,
            medianStrideLength: this.medianStepLength,
            weinbergK: this._weinbergK,
            sensitivity: this._sensitivity,
            isCalibrating: this.isCalibrating,
            isStationary: this._stationary.isStationary,
            activityState: this._activity.state,
            cadence: this._activity.cadence,
            walkingState: this._walkingState,
        };
    }

    _emitStep(timestamp) {
        this.dispatchEvent(new CustomEvent(`step`, { detail: { ...this._getEventData(), timestamp } }));
    }
}

// Centralized configuration (assigned outside class for older Safari compatibility)
Pedometer.CONFIG = {
    // Time constants (ms) for EMA filters
    tau: {
        baseline: 800,          // Baseline adaptation (slower to avoid chasing peaks)
        gravity: 600,           // Gravity estimation (slower to preserve step impulse)
        variance: 350,          // Variance estimation
        walkSignalFast: 100,    // Fast EMA for band-pass filter (captures step impulse)
        walkSignalSlow: 900,    // Slow EMA for band-pass filter (removes drift/rotation)
    },
    // Initialization windows (ms)
    init: {
        baseline: 600,          // Need 600ms before detection (reduced from 800 for faster start)
        varianceWarmup: 200,    // Additional warmup for stable variance
    },
    // Valley-Peak-Valley detection timing (MDPI paper approach)
    vpv: {
        minValleyToPeakMs: 80,  // Min time from valley to peak (very fast steps)
        maxValleyToPeakMs: 600, // Max time from valley to peak (extended for slow walk)
        minPeakToValleyMs: 80,  // Min time from peak to valley
        maxPeakToValleyMs: 600, // Max time from peak to valley (extended for slow walk)
        maxStepDurationMs: 1500,// Max total step duration (valley to valley, extended)
        valleyRatio: 0.4,       // Valley threshold = peakThreshold * valleyRatio (lowered for small steps)
    },
    // Walking state gate (prevents counting isolated bumps)
    walkingGate: {
        confirmSteps: 3,           // Need 3 step candidates to confirm walking
        confirmWindow: 3500,       // Max ms between first and last candidate (longer for slow walk)
        candidateWindow: 3500,     // Keep candidates for this duration
        candidateThresholdFactor: 0.70, // Threshold factor for idle candidates (lowered for small steps)
        walkingThresholdFactor: 0.50,   // Lower threshold when in walking state (rhythm confirmed)
        maxIntervalCV: 0.35,       // Max coefficient of variation for rhythm consistency (relaxed)
        minCadenceSpm: 35,         // Min plausible cadence (very slow hospital shuffle)
        maxCadenceSpm: 220,        // Max plausible cadence
        walkingTimeout: 3000,      // Drop to idle if no step for this long (ms)
        cooldownDuration: 450,     // Cooldown after large rotation detected
        rotationOmegaRad: 2.0,     // Rotation gate: rad/s (~115 deg/s)
        rotationLinMagMax: 1.2,    // Max linear accel (m/s²) during rotation gate
    },
    // Legacy hysteresis state machine (kept for compatibility)
    hysteresis: {
        lowFactor: 0.7,     // Arm at 70% of threshold
        highFactor: 1.0,    // Trigger at 100%
        confirmSamples: 2,  // Decrease samples to confirm peak
        armTimeout: 800,    // Max ms in armed state (wider for some devices)
        peakTimeout: 800,   // Max ms in peak state (wider for some devices)
        tolerance: 1e-2,    // Plateau detection tolerance
    },
    // Detection thresholds
    threshold: {
        minVariance: 0.001, // Minimum variance to confirm real motion (lowered for low-amplitude devices)
        minStepLength: 0.10,// Min step (shuffling gait)
    },
    // Step length estimation (dual-amplitude fusion for slow/wandering gait)
    stepLen: {
        bpKScale: 1.4,      // Scale factor for band-pass amplitude K (tune per device, 1.2-1.6)
    },
    // Buffer sizes
    buffer: {
        stepLength: 5,      // Median filter window (reduced for faster adaptation)
        maxSampleWindow: 2000, // Max ms of samples
        chartWindow: 60,    // Activity history points
        autoTune: 20,       // Auto-tune window
        cadence: 8,         // Cadence buffer size
    },
    // Sensitivity presets (tuned for band-pass filtered vertical signal)
    // maxStepLength restored to allow normal walking while amplitude-based K handles small steps
    sensitivity: {
        low: { peakThreshold: 0.6, minStepIntervalMs: 340, maxStepLength: 0.80 },
        medium: { peakThreshold: 0.4, minStepIntervalMs: 280, maxStepLength: 0.75 },
        high: { peakThreshold: 0.20, minStepIntervalMs: 200, maxStepLength: 0.70 },
        auto: { peakThreshold: 0.35, minStepIntervalMs: 260, maxStepLength: 0.75 },
    },
    // Cadence thresholds (steps per minute)
    cadenceThresholds: {
        verySlow: 40,       // < 40 = very slow / shuffling
        slow: 70,           // 40-70 = slow
        normal: 100,        // 70-100 = normal, >100 = fast
    },
    // Cadence correction factors for step length (aggressive reduction for small steps)
    cadenceFactors: {
        stationary: 0,
        verySlow: 0.65,    // Shuffling with tiny steps (was 0.80)
        slow: 0.80,        // Hospital walk (was 0.90)
        normal: 1.0,       // Normal indoor
        fast: 1.05,        // Brisk (reduced from 1.10)
        unknown: 0.80,     // Conservative default (was 0.90)
    },
    // Stationary detection thresholds
    stationary: {
        variance: 0.15,
        minLowVarianceDuration: 800,
        minNoStepDuration: 1500,
    },
};

export default Pedometer;
